import { useState } from 'react';
import { ApiError } from '@/api/client';
import { Icon } from '@/components/Icon';
import { cleanupAuditLogs } from './api';
import { useApi } from './kit/hooks';
import s from './AuditCleanupDialog.module.css';

const READONLY = import.meta.env.VITE_READONLY !== '0';

export function AuditCleanupDialog({ onClose, onDeleted, retainedCount }: { onClose: () => void; onDeleted: (count: number) => void; retainedCount?: number }) {
  const preview = useApi<{ cutoff: string; count: number }>('/api/v2/audit-logs/cleanup');
  const [phrase, setPhrase] = useState('');
  const [saving, setSaving] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [error, setError] = useState('');
  const confirm = async () => {
    if (READONLY || saving || uncertain || !preview.data || phrase !== '清理日志') return;
    setSaving(true); setError('');
    try {
      const result = await cleanupAuditLogs({ phrase, cutoff: preview.data.cutoff, expectedCount: preview.data.count });
      onDeleted(result.deletedCount);
    } catch (cause) {
      const unknown = !(cause instanceof ApiError) || cause.status === 0 || cause.status >= 500;
      setUncertain(unknown);
      setError(unknown ? '清理结果未确认。请关闭窗口并重新查看日志数量。' : cause instanceof Error ? cause.message : '清理失败');
      if (!unknown) preview.reload();
    } finally { setSaving(false); }
  };
  return <div className={s.backdrop} role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !saving) onClose(); }}><section className={s.dialog} role="dialog" aria-modal="true" aria-label="清理 90 天前的操作日志">
    <header><h2>清理 90 天前的日志</h2><button type="button" aria-label="关闭" disabled={saving} onClick={onClose}>×</button></header>
    <div className={s.body}>
      <div className={s.permanent}><Icon name="trash" size={16} /><div><strong>永久删除 {preview.data ? new Date(preview.data.cutoff).toLocaleDateString('zh-CN') : '90 天前'} 之前的 {preview.data?.count.toLocaleString('zh-CN') ?? '…'} 条日志</strong><p>删除后无法恢复，也不能再导出。审计需要留档的，请先点右上「导出」存一份。</p></div></div>
      {preview.loading && !preview.data ? <p>正在统计可清理的日志…</p> : preview.error ? <p role="alert" className={s.error}>统计失败：{preview.error.message} <button type="button" onClick={preview.reload}>重试</button></p> : null}
      <div className={s.summary}>
        {retainedCount != null && <div className={s.retained}><span>会保留近 90 天</span><b>{retainedCount.toLocaleString('zh-CN')} 条</b></div>}
        <div className={s.permission}><span>需要权限</span><b>audit.delete</b></div>
      </div>
      <label><span>输入「<strong>清理日志</strong>」确认</span><input autoFocus value={phrase} onChange={(event) => setPhrase(event.target.value)} placeholder="清理日志" disabled={saving || READONLY} /></label>
      <p className={s.ipadHint}>打对了「永久清理」才可点</p>
      <p className={s.phoneHint}>这类操作很少用，所以要求手打确认，而不是点一下按钮。</p>
      {READONLY && <p>当前是只读预览，不能执行清理。</p>}
      {error && <p role="alert" className={s.error}>{error}</p>}
    </div>
    <footer><button type="button" onClick={onClose} disabled={saving}>取消</button><button type="button" className={s.danger} disabled={READONLY || saving || uncertain || !preview.data || !preview.data.count || phrase !== '清理日志'} onClick={() => void confirm()}>{saving ? '清理中…' : '永久清理'}</button></footer>
  </section></div>;
}
